import React, { useState, useEffect, useMemo, useCallback } from "react";
import { X, User, Mail, Phone, MapPin, Loader2, DollarSign, Plus, Calendar, Bookmark, Tag, Briefcase, FileText, CheckCircle2, XCircle, SlidersHorizontal, Database } from "lucide-react";
import { capitalizeEachWord, cleanLeadMessageText, formatOfferSubmissionSummary } from "../../helper/function";
import moment from "moment";
import toast from "react-hot-toast";
import {
    getPropertyOffers,
    getPropertyVendorStatus,
} from "../../api/propertyManagement";
import OfferWorkflowActions from "../../features/offers/components/OfferWorkflowActions";
import PropertySearchPicker from "../common/PropertySearchPicker";
import AgentNotesSummary from "../notes/AgentNotesSummary";

// Loaded on demand: it reaches for the Database API, and this modal is rendered
// on pages (and in tests) that never open it.
const AddToDatabaseModal = React.lazy(() => import("../../features/leads/components/AddToDatabaseModal"));
// Same reason: the series reaches for the agent API. Only loaded when the caller
// hands over the lead reminder functions below.
const AppraisalReminderSeries = React.lazy(() => import("../../features/leads/components/AppraisalReminderSeries"));

/**
 * Extract just the buyer's message from offer initial_notes.
 * The format is: "[Buyer Portal Offer Submission]\n\nReference: ...\n{details}\n\nMessage: {actual message}"
 * We only want the part after "Message: " (the buyer's actual comment to the agent).
 */
const extractOfferMessage = (initialNotes) => {
    if (!initialNotes) return "No message provided";
    
    // Look for "Message: " and extract everything after it
    const messageMatch = initialNotes.match(/Message:\s*(.+?)(?:\n\n\[|$)/s);
    if (messageMatch && messageMatch[1]) {
        const msg = messageMatch[1].trim();
        return msg === "No message" ? "No message provided" : msg;
    }
    
    return "No message provided";
};

const formatMoney = (val) => {
    if (val === undefined || val === null || val === "") return null;
    let numericVal = val;
    if (typeof val === 'string') {
        numericVal = val.replace(/[^\d.]/g, '');
    }
    const parsed = parseFloat(numericVal);
    if (isNaN(parsed) || parsed === 0) return null;

    return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0
    }).format(parsed);
};

const StatusBadge = ({ status }) => {
    const statusColors = {
        SUBMITTED: "bg-blue-50 text-blue-700 border-blue-100",
        IN_PROGRESS: "bg-amber-50 text-amber-700 border-amber-100",
        RESOLVED: "bg-emerald-50 text-emerald-700 border-emerald-100",
        UNDER_REVIEW: "bg-purple-50 text-purple-700 border-purple-100",
        ACCEPTED: "bg-emerald-50 text-emerald-700 border-emerald-100",
        REJECTED: "bg-red-50 text-red-700 border-red-100",
        COUNTER_OFFER: "bg-orange-50 text-orange-700 border-orange-100",
        WITHDRAWN: "bg-gray-50 text-gray-600 border-gray-100",
        EXPIRED: "bg-gray-50 text-gray-500 border-gray-100",
    };
    const cls = statusColors[status] || "bg-gray-50 text-gray-600 border-gray-100";
    const displayMap = {
        SUBMITTED: "Submitted", IN_PROGRESS: "In Progress", RESOLVED: "Resolved",
        UNDER_REVIEW: "Under Review", ACCEPTED: "Accepted", REJECTED: "Declined",
        COUNTER_OFFER: "Counter Offer", WITHDRAWN: "Withdrawn", EXPIRED: "Expired",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black border uppercase tracking-wider ${cls}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            {displayMap[status] || status}
        </span>
    );
};

const SectionHeader = ({ children }) => (
    <div className="bg-[#f8f9fa] py-2.5 px-6 rounded-xl mb-6 border border-gray-100 shadow-sm first:mt-0 mt-8">
        <h3 className="text-[13px] font-bold text-gray-800 tracking-tight">{children}</h3>
    </div>
);

const LeadPreviewModal = ({
    isOpen,
    onClose,
    lead,
    enquiry,
    offer,
    mode = "assign",
    onAssign,
    onSaveEnquiry,
    onSaveOffer,
    onAddNote,
    onIgnore,
    /** Called with the lead after "Add to Database" saves, so the parent can drop it from the holding area (see onIgnore) */
    onDatabaseSaved,
    /** Called after an offer is declined so the parent can drop the lead from the holding area + close */
    onDecline,
    /** Called with the lead when the agent wants to edit this contact's buying preferences (Enquiries/Inspections only) */
    onOpenPreferences,
    getProperties,
    getAgents,
    loading = false,
    currentUserName = "Agent", // Name of the current user for notes
    /** Called when send-to-vendor / send-to-buyer / finalize updates the linked property offer (keeps property pages in sync) */
    onWorkflowOfferUpdated,
    /**
     * The lead's call-back reminder series (api/lead getLeadReminders /
     * createLeadReminders / deleteLeadReminder). Passed in rather than imported
     * so this modal's dependency graph stays as it was. Without them the series
     * is not shown. Christian Gravias, Monday 13166757620: "Add reminder series
     * to buyers enquiries as well".
     */
    fetchLeadReminders,
    bookLeadReminders,
    deleteLeadReminder,
}) => {
    const [agents, setAgents] = useState([]);
    // The listing this enquiry gets filed onto. Held as the whole property so the
    // picker can show its address once chosen, not just an id.
    const [selectedProperty, setSelectedProperty] = useState(null);
    const selectedPropertyId = selectedProperty?.id ? String(selectedProperty.id) : "";
    // "Add to Database" — files the person behind the enquiry into a Database
    // category without having to assign them to a property first.
    const [dbLead, setDbLead] = useState(null);
    const [notes, setNotes] = useState("");
    const [addNoteLoading, setAddNoteLoading] = useState(false);
    // Call-backs staged under the note, booked by the same button.
    const [reminders, setReminders] = useState([]);
    // Bumped after a booking so the series re-reads its "Already booked" list.
    const [reminderKey, setReminderKey] = useState(0);

    /** Linked property offer + vendor workflow (Buyer Offers holding tab + property offer modal) */
    const [resolvedOffer, setResolvedOffer] = useState(null);
    const [resolvedOfferLoading, setResolvedOfferLoading] = useState(false);
    const [resolvedOfferError, setResolvedOfferError] = useState(false);
    const [vendorStatus, setVendorStatus] = useState(null);
    // Patch applied to the resolved offer after a workflow action, so the modal
    // body figures stay in step with what OfferWorkflowActions just changed.
    const [workflowPatch, setWorkflowPatch] = useState(null);

    const [leadForm, setLeadForm] = useState({
        name: "", email: "", phone: "", address: "",
        type: "", intent: "", budget_min: "", budget_max: "",
        timeline: "", agent: "", source: "Buyer Portal", priority: "Medium",
        commission: "", gained_commission: "", settlement_date: ""
    });

    const [enqForm, setEnqForm] = useState({
        name: "", email: "", phone: "", message: "", agent_notes: "", status: "SUBMITTED"
    });

    const [offerForm, setOfferForm] = useState({
        status: "SUBMITTED",
        agent_notes: "",
        counter_amount: "",
        buyer_name: "",
        buyer_email: "",
        buyer_phone: "",
        home_address: "",
        offer_amount: "",
        conditions: [],
        settlement_days: "",
        custom_settlement_date: "",
        deposit_amount: "",
        message: ""
    });

    const isOffer = mode === "view_offer" || (lead?.lead_type === "offer");
    // Somebody answering a recruitment ad. Every listing-shaped action below is
    // meaningless for them - they are not enquiring about a property, they have
    // no buying preferences, and there is nothing to add them to
    // (Christian Gravias, Monday 12889580221).
    const isAgentApplication = lead?.lead_type === "agent_application";

    useEffect(() => {
        if (!isOpen) return;

        // Properties are no longer pre-loaded here: PropertySearchPicker searches
        // the whole CRM server-side, so the old "first 100 of my listings" fetch
        // was both the wrong set and unnecessary.
        if (mode === "assign") {
            if (getAgents && agents.length === 0) {
                getAgents().then(res => {
                    setAgents(res.data?.results || res.data || []);
                }).catch(() => setAgents([]));
            }
        }
    }, [isOpen, mode, getProperties, getAgents]); // Removed volatile deps like lead, enquiry, offer

    // Separate useEffect for form synchronization
    useEffect(() => {
        if (!isOpen) return;

        if (mode === "assign") {
            // Format settlement_date strictly as YYYY-MM-DD
            const rawDate = lead?.settlement_date;
            const formattedDate = (rawDate && moment(rawDate).isValid())
                ? moment(rawDate).format("YYYY-MM-DD")
                : null;

            setLeadForm({
                name: lead?.full_name || lead?.name || "",
                email: lead?.email || "",
                phone: lead?.contact || lead?.phone || "",
                address: lead?.property_address || "",
                type: lead?.buyer_property_type || "",
                intent: lead?.intent || "",
                budget_min: lead?.buyer_budget_min || lead?.price_range?.split('-')[0]?.trim() || "",
                budget_max: lead?.buyer_budget_max || lead?.price_range?.split('-')[1]?.trim() || "",
                timeline: lead?.timeline || "",
                agent: lead?.agent || "",
                source: lead?.source || "Buyer Portal",
                priority: lead?.priority || "Medium",
                commission: lead?.commission || "",
                gained_commission: lead?.gained_commission || "",
                settlement_date: formattedDate
            });

            // A lead that already names a property (a portal enquiry on a listing)
            // opens with it pre-picked. The address on the lead is enough to show
            // it - the picker only needs the id to file the enquiry.
            const initialPropId = lead?.property || lead?.property_id || "";
            setSelectedProperty(
                initialPropId
                    ? { id: initialPropId, address: lead?.property_address || "", title: "" }
                    : null
            );
            setNotes("");
            setReminders([]);

            // For offer-type leads in assign mode, also populate offerForm with offer data from the lead
            if (lead?.lead_type === "offer") {
                const customDate = lead?.custom_settlement_date;
                const formattedCustomDate = (customDate && moment(customDate).isValid())
                    ? moment(customDate).format("YYYY-MM-DD")
                    : "";
                
                // Format deposit as integer (remove decimals)
                const depositVal = lead?.deposit_amount;
                const formattedDeposit = depositVal ? Math.round(parseFloat(depositVal)) : "";
                
                // Format offer amount as integer
                const offerVal = lead?.offer_amount || lead?.offer_amount_min;
                const formattedOffer = offerVal ? Math.round(parseFloat(offerVal)) : "";
                
                setOfferForm({
                    status: "SUBMITTED",
                    agent_notes: "",
                    counter_amount: "",
                    buyer_name: lead?.full_name || lead?.name || "",
                    buyer_email: lead?.email || "",
                    buyer_phone: lead?.contact || lead?.phone || "",
                    home_address: lead?.property_address || "",
                    offer_amount: formattedOffer,
                    conditions: lead?.offer_conditions || [],
                    settlement_days: lead?.settlement_days || "",
                    custom_settlement_date: formattedCustomDate,
                    deposit_amount: formattedDeposit,
                    message: extractOfferMessage(lead?.initial_notes),
                });
            }
        }
        if (mode === "view_enquiry" && enquiry) {
            setEnqForm({
                name: enquiry.name || enquiry.full_name || "",
                email: enquiry.email || "",
                phone: enquiry.phone || enquiry.contact || "",
                message: cleanLeadMessageText(enquiry.message),
                agent_notes: enquiry.agent_notes || "",
                status: enquiry.status || "SUBMITTED",
            });
        }
        if (mode === "view_offer" && offer) {
            // Format deposit and offer amounts as integers
            const depositVal = offer.deposit_amount;
            const offerDepositFormatted = depositVal ? Math.round(parseFloat(depositVal)) : "";
            const offerAmountVal = offer.offer_amount || offer.offer_amount_min;
            const offerAmountFormatted = offerAmountVal ? Math.round(parseFloat(offerAmountVal)) : "";
            
            setOfferForm({
                status: offer.status || "SUBMITTED",
                agent_notes: offer.agent_notes || "",
                counter_amount: offer.counter_amount || "",
                buyer_name: offer.buyer_name || "",
                buyer_email: offer.buyer_email || "",
                buyer_phone: offer.buyer_phone || "",
                home_address: offer.home_address || "",
                offer_amount: offerAmountFormatted,
                conditions: offer.conditions || [],
                settlement_days: offer.settlement_days || "",
                custom_settlement_date: offer.custom_settlement_date ? moment(offer.custom_settlement_date).format("YYYY-MM-DD") : "",
                deposit_amount: offerDepositFormatted,
                message: offer.message || "",
            });
        }
    }, [isOpen, mode, lead, enquiry, offer]);

    // Memoised on the lead id: the series reloads whenever these change, so a
    // fresh closure every render would have it fetching in a loop.
    const reminderLeadId = mode === "assign" ? lead?.id : null;
    const showReminderSeries = !!(reminderLeadId && fetchLeadReminders && bookLeadReminders);
    const fetchBooked = useCallback(
        () => fetchLeadReminders(reminderLeadId),
        [fetchLeadReminders, reminderLeadId]
    );
    const deleteBooked = useCallback(
        (kind, id) => deleteLeadReminder(reminderLeadId, kind, id),
        [deleteLeadReminder, reminderLeadId]
    );

    const propertyId = useMemo(() => {
        if (mode === "view_offer" && offer?.property != null) {
            const p = offer.property;
            return typeof p === "object" && p !== null ? p.id : p;
        }
        if (mode === "assign" && lead?.property != null) {
            const p = lead.property;
            return typeof p === "object" && p !== null ? p.id : p;
        }
        return null;
    }, [mode, offer, lead]);

    useEffect(() => {
        if (!isOpen) {
            // Otherwise the property picked for the last enquiry is still selected
            // when the next one is opened.
            setSelectedProperty(null);
            setDbLead(null);
            setResolvedOffer(null);
            setResolvedOfferLoading(false);
            setResolvedOfferError(false);
            setVendorStatus(null);
            setWorkflowPatch(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || mode !== "assign" || lead?.lead_type !== "offer" || !propertyId || !lead?.offer_reference) {
            return;
        }
        let cancelled = false;
        setResolvedOfferLoading(true);
        setResolvedOfferError(false);
        getPropertyOffers(propertyId)
            .then((res) => {
                if (cancelled) return;
                const list = res?.data?.results || res?.data || [];
                const found = list.find((o) => o.reference_number === lead.offer_reference);
                if (found) {
                    setResolvedOffer(found);
                    setOfferForm((p) => ({
                        ...p,
                        status: found.status || p.status,
                        agent_notes: found.agent_notes || p.agent_notes,
                    }));
                } else {
                    setResolvedOfferError(true);
                    setResolvedOffer(null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setResolvedOfferError(true);
                    setResolvedOffer(null);
                }
            })
            .finally(() => {
                if (!cancelled) setResolvedOfferLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, mode, propertyId, lead?.offer_reference, lead?.lead_type]);

    useEffect(() => {
        if (!isOpen || !propertyId) return;
        getPropertyVendorStatus(propertyId)
            .then((r) => setVendorStatus(r?.data || null))
            .catch(() => setVendorStatus(null));
    }, [isOpen, propertyId]);

    // Fold a workflow action's result (from OfferWorkflowActions) back into the
    // modal so the body figures + status badge update without a reopen.
    const applyOfferUpdate = useCallback((data) => {
        if (!data) return;
        setWorkflowPatch((p) => ({ ...(p || {}), ...data }));
        if (mode === "assign" && data.id) setResolvedOffer((prev) => (prev ? { ...prev, ...data } : data));
        onWorkflowOfferUpdated?.(data);
        if (data.status) setOfferForm((p) => ({ ...p, status: data.status }));
    }, [mode, onWorkflowOfferUpdated]);

    if (!isOpen) return null;

    const baseOffer = mode === "view_offer" ? offer : resolvedOffer;
    const effectiveOffer =
        baseOffer && workflowPatch && baseOffer.id === workflowPatch.id
            ? { ...baseOffer, ...workflowPatch }
            : baseOffer;

    const showOfferWorkflow =
        isOffer &&
        propertyId &&
        (mode === "view_offer" || (mode === "assign" && !!lead?.offer_reference));

    const displayName =
        mode === "assign" ? (lead?.full_name || lead?.name || "Unknown") :
            mode === "view_enquiry" ? enqForm.name :
                offerForm.buyer_name;

    const displayEmail =
        mode === "assign" ? lead?.email :
            mode === "view_enquiry" ? enqForm.email :
                offerForm.buyer_email;

    const displayPhone =
        mode === "assign" ? (lead?.contact || lead?.phone) :
            mode === "view_enquiry" ? enqForm.phone :
                offerForm.buyer_phone;

    // Parse initial_notes to separate original message from legacy appended notes
    const parseInitialNotes = (initialNotes) => {
        if (!initialNotes) return { originalMessage: '', legacyNotes: [] };
        
        // Pattern to find timestamps like [30/3/2026 2:59 pm] or [31/3/2026 4:30 am]
        const timestampPattern = /\s*\[(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*[ap]m)\]\s*/gi;
        
        // Split by timestamp pattern
        const parts = initialNotes.split(timestampPattern);
        
        if (parts.length <= 1) {
            // No timestamps found - entire thing is the original message
            return { originalMessage: initialNotes, legacyNotes: [] };
        }
        
        // First part is the original message
        const originalMessage = parts[0].trim();
        
        // Extract legacy notes (timestamp + content pairs)
        const legacyNotes = [];
        for (let i = 1; i < parts.length; i += 2) {
            const timestamp = parts[i];
            const content = parts[i + 1]?.trim() || '';
            if (timestamp && content) {
                legacyNotes.push({ timestamp, content, author: 'Agent' });
            }
        }
        
        return { originalMessage, legacyNotes };
    };
    
    const parsedNotes = mode === "assign" ? parseInitialNotes(lead?.initial_notes) : { originalMessage: '', legacyNotes: [] };
    
    const displayMessage =
        mode === "assign" 
            ? (isOffer ? extractOfferMessage(lead?.initial_notes) : parsedNotes.originalMessage || lead?.initial_notes) 
            : mode === "view_enquiry" ? enqForm.message :
                (offerForm.message ? extractOfferMessage(offerForm.message) : "No message provided");

    const displayAddress =
        mode === "assign" ? lead?.property_address :
            mode === "view_offer" ? offerForm.home_address :
                null;

    const receivedAt =
        mode === "assign" ? lead?.created_at :
            mode === "view_enquiry" ? enquiry?.created_at :
                offer?.created_at;


    const plural = (n) => `${n} reminder${n === 1 ? "" : "s"}`;

    // Books the staged series against the lead. Never throws, so a note that has
    // already saved is not reported as failed because of it. Returns the count.
    const bookStagedReminders = async (content) => {
        if (!showReminderSeries || reminders.length === 0) return 0;
        try {
            await bookLeadReminders(
                lead.id,
                reminders.map((entry) => ({ date: entry.date.toISOString(), reason: entry.reason })),
                content
            );
            const booked = reminders.length;
            setReminders([]);
            setReminderKey((k) => k + 1);
            toast.success(`${plural(booked)} booked.`);
            return booked;
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    (content ? "Note saved, but the reminders could not be booked." : "Failed to book the reminders.")
            );
            return 0;
        }
    };

    const handleAddNote = async () => {
        const newNoteText = (notes || "").trim();
        if (!newNoteText && reminders.length > 0) {
            // Reminders only: nothing to append to the thread.
            setAddNoteLoading(true);
            try {
                await bookStagedReminders("");
            } finally {
                setAddNoteLoading(false);
            }
            return;
        }
        if (!onAddNote || !newNoteText) return;
        setAddNoteLoading(true);
        try {
            if (mode === "assign") {
                // For assign mode (holding area), pass just the raw note text
                // The parent handler (LeadsPage) will handle the JSON structure
                await onAddNote(newNoteText);
                // The note is safe; now the call-backs, carrying it as their reason.
                setNotes("");
                await bookStagedReminders(newNoteText);
            } else {
                // For view_enquiry/view_offer, build text-based combined string
                const timestamp = moment().format('D/M/YYYY h:mm a');
                let previousNotes = "";
                if (mode === "view_enquiry") previousNotes = enquiry?.agent_notes || "";
                else if (mode === "view_offer") previousNotes = offer?.agent_notes || "";
                const authorName = currentUserName || "Agent";
                const combined = (previousNotes ? previousNotes + "\n\n" : "") + `[${timestamp}] (${authorName}) ${newNoteText}`;
                await onAddNote(combined);
                // Update local form state so UI refreshes immediately
                if (mode === "view_enquiry") setEnqForm(prev => ({ ...prev, agent_notes: combined }));
                else if (mode === "view_offer") setOfferForm(prev => ({ ...prev, agent_notes: combined }));
            }
            setNotes("");
        } catch (err) {
            console.error("Failed to add note", err);
        } finally {
            setAddNoteLoading(false);
        }
    };

    return (
        // No close on a background click: this panel holds the Add Note box and
        // the editable enquiry fields, and a stray click outside the card used to
        // throw the lot away (Christian Gravias, 24 Aug 2026). Closing is the X.
        <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-md transition-all duration-300"
        >
            <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-[0_30px_70px_-15px_rgba(0,0,0,0.3)] overflow-hidden border border-white/20">
                {/* Header Section */}
                <div className="flex items-center justify-between px-10 py-7 bg-white border-b border-gray-100 flex-shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                            {mode === "assign"
                                ? (isOffer ? "View Buyer Offer" : "View Buyer Enquiry")
                                : (isOffer ? "Offer Details" : "Enquiry Details")}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-300 hover:text-gray-600 transition-all duration-200"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-10 py-8 bg-white custom-scrollbar">
                    {/* TOP SUMMARY */}
                    <div className="mb-10 group relative">
                        <div className="flex gap-8 bg-[#55C5D0]/5 border border-[#55C5D0]/10 rounded-3xl p-8 transition-all hover:bg-[#55C5D0]/10">
                            <div className="flex-shrink-0 w-24 h-28 bg-white rounded-2xl flex items-center justify-center border border-[#55C5D0]/20 shadow-sm relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-[#55C5D0]/10 to-transparent opacity-50" />
                                <User size={48} className="text-[#55C5D0] relative z-10" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <h3 className="text-2xl font-black text-gray-900 mb-2 truncate">
                                    {displayName}
                                </h3>
                                <div className="flex flex-wrap gap-x-8 gap-y-2 mb-4">
                                    {displayEmail && (
                                        <div className="text-xs font-bold text-gray-500 flex items-center gap-2">
                                            <Mail size={14} className="text-[#55C5D0]" />
                                            {displayEmail}
                                        </div>
                                    )}
                                    {displayPhone && (
                                        <div className="text-xs font-bold text-gray-500 flex items-center gap-2">
                                            <Phone size={14} className="text-[#55C5D0]" />
                                            {displayPhone}
                                        </div>
                                    )}
                                </div>
                                {displayAddress && (
                                    <div className="text-[11px] font-black text-[#4a9ba3] flex items-center gap-2 uppercase tracking-widest bg-white/80 py-2 px-4 rounded-xl border border-gray-100 w-fit">
                                        <MapPin size={13} /> {displayAddress}
                                    </div>
                                )}
                                {displayMessage && (
                                    <div className="mt-4 bg-white/80 p-4 rounded-xl border border-[#55C5D0]/10 text-xs font-semibold text-gray-700 leading-relaxed shadow-sm italic">
                                        "{displayMessage}"
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Data Sections */}
                    <div className="space-y-4">
                        {/* 1. Contact Details - 4 COLUMNS */}
                        <section>
                            <SectionHeader>Contact Details</SectionHeader>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6">
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">First Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={displayName.split(' ')[0] || ""}
                                        readOnly
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:ring-4 focus:ring-[#55C5D0]/10 transition-all cursor-default"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Last Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={displayName.split(' ').slice(1).join(' ') || ""}
                                        readOnly
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none cursor-default"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Email <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={displayEmail || ""}
                                        readOnly
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none cursor-default"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Phone <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={displayPhone || ""}
                                        readOnly
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none cursor-default"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* 2. Enquiry Details */}
                        <section>
                            <SectionHeader>Enquiry Details</SectionHeader>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Lead Type</label>
                                    <div className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
                                        {isOffer ? "Buyer Offer" : "Buyer Enquiry"}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Source</label>
                                    <div className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
                                        {leadForm.source || "Buyer Portal"}
                                    </div>
                                </div>
                                {mode === "assign" && (
                                    <div className="space-y-2">
                                        <label className="text-[12px] font-bold text-gray-500">Selling Time Frame</label>
                                        <div className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
                                            {leadForm.timeline || "Not specified"}
                                        </div>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Received</label>
                                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
                                        {receivedAt ? moment(receivedAt).format("ddd D MMM, h:mma") : "N/A"}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Assigned Agent</label>
                                    <select
                                        value={leadForm.agent}
                                        onChange={(e) => setLeadForm(p => ({ ...p, agent: e.target.value }))}
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:ring-4 focus:ring-[#55C5D0]/10 transition-all border-[#E4E6E9]"
                                    >
                                        <option value="">Select an agent...</option>
                                        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-[12px] font-bold text-gray-500">Status</label>
                                    <select
                                        value={mode === "view_enquiry" ? enqForm.status : (mode === "view_offer" ? offerForm.status : "SUBMITTED")}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (mode === "view_enquiry") setEnqForm(p => ({ ...p, status: val }));
                                            else if (mode === "view_offer") setOfferForm(p => ({ ...p, status: val }));
                                        }}
                                        className="w-full px-4 py-3 bg-white border border-[#E4E6E9] rounded-xl text-sm font-medium text-gray-700 outline-none focus:ring-4 focus:ring-[#55C5D0]/10"
                                    >
                                        <option value="SUBMITTED">Submitted</option>
                                        <option value="IN_PROGRESS">In Progress</option>
                                        <option value="RESOLVED">Resolved</option>
                                        {isOffer && (
                                            <>
                                                <option value="ACCEPTED">Accepted</option>
                                                <option value="REJECTED">Rejected</option>
                                                <option value="COUNTER_OFFER">Counter Offer</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                            </div>
                        </section>

                        {/* 3. Property & Offer Information */}
                        <section className={isAgentApplication ? "hidden" : undefined}>
                            <SectionHeader>{isOffer ? 'Offer Details & Negotiation' : 'Property Information'}</SectionHeader>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Property Address</label>
                                    <input
                                        type="text"
                                        value={leadForm.address || "N/A"}
                                        readOnly
                                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700"
                                    />
                                </div>
                                {isOffer ? (
                                    <div className="space-y-2">
                                        <label className="text-[12px] font-bold text-gray-500">Offer Amount (Single Price)</label>
                                        <div className="relative">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
                                            <input
                                                type="number"
                                                value={offerForm.offer_amount}
                                                readOnly
                                                className="w-full pl-8 pr-4 py-3 rounded-xl text-lg font-black outline-none transition-all bg-white border border-gray-200 text-[#374151] cursor-default"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <label className="text-[12px] font-bold text-gray-500">Property Type</label>
                                        <input
                                            type="text"
                                            value={leadForm.type || ""}
                                            onChange={(e) => setLeadForm(p => ({ ...p, type: e.target.value }))}
                                            className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:ring-4 focus:ring-[#55C5D0]/10 transition-all border-[#E4E6E9]"
                                            placeholder="e.g. Apartment, House"
                                        />
                                    </div>
                                )}

                                {isOffer && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-[12px] font-bold text-gray-500">Settlement Period (Days)</label>
                                            <input
                                                type="number"
                                                value={offerForm.settlement_days}
                                                readOnly
                                                placeholder="N/A"
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none cursor-default"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[12px] font-bold text-gray-500">Custom Settlement Date</label>
                                            <div className="relative">
                                                <Calendar size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="date"
                                                    value={offerForm.custom_settlement_date}
                                                    readOnly
                                                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none cursor-default"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[12px] font-bold text-gray-500">Deposit Amount</label>
                                            <div className="relative">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold font-mono">$</div>
                                                <input
                                                    type="number"
                                                    value={offerForm.deposit_amount}
                                                    readOnly
                                                    className="w-full pl-8 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none cursor-default"
                                                />
                                            </div>
                                        </div>
                                        {offerForm.conditions && offerForm.conditions.length > 0 && (
                                            <div className="md:col-span-2 space-y-2">
                                                <label className="text-[12px] font-bold text-gray-500">Offer Conditions</label>
                                                <div className="flex flex-wrap gap-2">
                                                    {offerForm.conditions.map((cond, i) => (
                                                        <span key={i} className="px-4 py-2 bg-[#55C5D0]/5 border border-[#55C5D0]/20 rounded-xl text-xs font-bold text-[#374151] shadow-sm flex items-center gap-2">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-[#55C5D0]" />
                                                            {typeof cond === 'string' ? cond : (cond.type?.replace(/_/g, ' ') || 'Condition')}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                {!isOffer && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-[12px] font-bold text-gray-500">Intent</label>
                                            <input
                                                type="text"
                                                value={leadForm.intent || ""}
                                                onChange={(e) => setLeadForm(p => ({ ...p, intent: e.target.value }))}
                                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none focus:ring-4 focus:ring-[#55C5D0]/10 transition-all border-[#E4E6E9]"
                                                placeholder="e.g. Buying, Investing"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[12px] font-bold text-gray-500">Price Expectation</label>
                                            <div className="grid grid-cols-2 gap-4">
                                                <input
                                                    type="text"
                                                    value={leadForm.budget_min}
                                                    onChange={(e) => setLeadForm(p => ({ ...p, budget_min: e.target.value }))}
                                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none border-[#E4E6E9]"
                                                    placeholder="Min"
                                                />
                                                <input
                                                    type="text"
                                                    value={leadForm.budget_max}
                                                    onChange={(e) => setLeadForm(p => ({ ...p, budget_max: e.target.value }))}
                                                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 outline-none border-[#E4E6E9]"
                                                    placeholder="Max"
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </section>

                        {showOfferWorkflow && (
                            <section>
                                <SectionHeader>Listing &amp; negotiation</SectionHeader>
                                {vendorStatus && (
                                    <div className="mb-4 flex flex-wrap gap-2 text-[11px]">
                                        {vendorStatus.state === "active" && (
                                            <span className="px-3 py-1.5 rounded-lg border bg-emerald-50 text-emerald-800 border-emerald-100 font-bold">
                                                Vendor portal active
                                                {vendorStatus.vendor?.email ? ` · ${vendorStatus.vendor.email}` : ""}
                                            </span>
                                        )}
                                        {vendorStatus.state === "pending" && (
                                            <span className="px-3 py-1.5 rounded-lg border bg-amber-50 text-amber-800 border-amber-100 font-bold">
                                                Vendor invite pending
                                                {vendorStatus.vendor?.email ? ` · ${vendorStatus.vendor.email}` : ""}
                                            </span>
                                        )}
                                        {vendorStatus.state === "not_invited" && (
                                            <span className="px-3 py-1.5 rounded-lg border bg-gray-50 text-gray-600 border-gray-100 font-bold">
                                                Vendor not invited yet
                                                {vendorStatus.owner_email && ` · Owner: ${vendorStatus.owner_email}`}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {mode === "assign" && resolvedOfferLoading && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                                        <Loader2 size={18} className="animate-spin text-[#55C5D0]" />
                                        Loading linked offer from property…
                                    </div>
                                )}

                                {mode === "assign" && resolvedOfferError && !resolvedOfferLoading && (
                                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-4 mb-2">
                                        Could not find this offer on the listing (reference {lead?.offer_reference || "—"}). Open the
                                        property&apos;s Offers tab, or add this lead to the property first.
                                    </p>
                                )}

                                {effectiveOffer && (
                                    <>
                                        <div className="flex flex-wrap items-center gap-3 mb-3">
                                            <span className="text-[11px] font-black text-gray-500 uppercase tracking-widest">Offer status</span>
                                            <StatusBadge status={effectiveOffer.status} />
                                            <span className="text-xs text-gray-400 font-mono">{effectiveOffer.reference_number}</span>
                                        </div>

                                        <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-widest mb-3">
                                            {effectiveOffer.sent_to_vendor_at ? (
                                                <span className="px-2 py-1 rounded-md border bg-amber-50 text-amber-700 border-amber-100">
                                                    ✓ Sent to vendor {moment(effectiveOffer.sent_to_vendor_at).fromNow()}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded-md border bg-gray-50 text-gray-500 border-gray-100">
                                                    Not yet sent to vendor
                                                </span>
                                            )}
                                            {effectiveOffer.sent_to_buyer_at ? (
                                                <span className="px-2 py-1 rounded-md border bg-emerald-50 text-emerald-700 border-emerald-100">
                                                    ✓ Sent to buyer {moment(effectiveOffer.sent_to_buyer_at).fromNow()}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded-md border bg-gray-50 text-gray-500 border-gray-100">
                                                    Not yet sent to buyer
                                                </span>
                                            )}
                                        </div>

                                        {["ACCEPTED", "REJECTED", "EXPIRED", "WITHDRAWN", "BUYER_WITHDRAWN"].includes(effectiveOffer.status) && (
                                            <div
                                                className={`mb-4 p-3 rounded-xl border-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${
                                                    effectiveOffer.status === "ACCEPTED"
                                                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                                                        : "bg-gray-100 border-gray-200 text-gray-600"
                                                }`}
                                            >
                                                {effectiveOffer.status === "ACCEPTED" ? (
                                                    <>
                                                        <CheckCircle2 size={16} />
                                                        <span>Locked — offer accepted, property is Under Contract.</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <XCircle size={16} />
                                                        <span>
                                                            Locked — {effectiveOffer.status_display || effectiveOffer.status}. No further negotiation possible.
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>
                        )}

                        {/* 4. Add to Property Enquiries — offers are auto-filed onto the listing, so only enquiries need manual filing */}
                        {mode === "assign" && !isOffer && !isAgentApplication && (
                            <section>
                                <SectionHeader>Add to Property Enquiries</SectionHeader>
                                <div className="space-y-2">
                                    <label className="text-[12px] font-bold text-gray-500">Search for the property to add this enquiry to</label>
                                    <PropertySearchPicker
                                        selected={selectedProperty}
                                        onSelect={setSelectedProperty}
                                        fetchProperties={getProperties}
                                        placeholder="Start typing an address or suburb, e.g. 39A Beech Street"
                                    />
                                    <p className="text-[11px] text-gray-400">
                                        Searches every property in the CRM, not just your own listings.
                                    </p>
                                </div>
                            </section>
                        )}

                        {/* 5. Original Message & Notes */}
                        <section>
                            <SectionHeader>
                                {isAgentApplication ? 'Application Details' : 'Original Enquiry Message'}
                            </SectionHeader>
                            <div className="bg-gray-50/80 border border-gray-100 rounded-2xl p-6 mb-6 shadow-inner">
                                <p className="text-sm font-medium text-gray-600 leading-relaxed italic">
                                    "{displayMessage}"
                                </p>
                            </div>

                            {/* Agent Notes - Chat-like display for assign mode */}
                            {mode === "assign" && (
                                (Array.isArray(lead?.agent_notes) && lead.agent_notes.length > 0) || 
                                (parsedNotes.legacyNotes && parsedNotes.legacyNotes.length > 0)
                            ) && (
                                <div className="mb-6">
                                    <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest mb-3">Agent Notes</p>
                                    {/* AI summary of the notes below (Monday 13202770775) */}
                                    <AgentNotesSummary
                                        notes={[...(parsedNotes.legacyNotes || []), ...(Array.isArray(lead?.agent_notes) ? lead.agent_notes : [])]}
                                        subject={lead?.name || lead?.full_name}
                                        className="mb-3"
                                    />
                                    <div className="bg-gray-50/80 border border-gray-200 rounded-2xl p-4 space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
                                        {/* New JSON-based notes (newest first) */}
                                        {Array.isArray(lead?.agent_notes) && [...lead.agent_notes].reverse().map((note, i) => (
                                            <div key={`new-${i}`} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-7 h-7 rounded-full bg-[#55C5D0]/20 flex items-center justify-center">
                                                        <User size={14} className="text-[#55C5D0]" />
                                                    </div>
                                                    <span className="text-xs font-bold text-gray-700">{note.author || 'Agent'}</span>
                                                    <span className="text-[10px] text-gray-400 ml-auto">{note.timestamp}</span>
                                                </div>
                                                <p className="text-sm text-gray-700 leading-relaxed pl-9">{note.content}</p>
                                            </div>
                                        ))}
                                        {/* Legacy notes from initial_notes (oldest first, so reverse) */}
                                        {parsedNotes.legacyNotes && [...parsedNotes.legacyNotes].reverse().map((note, i) => (
                                            <div key={`legacy-${i}`} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="w-7 h-7 rounded-full bg-[#55C5D0]/20 flex items-center justify-center">
                                                        <User size={14} className="text-[#55C5D0]" />
                                                    </div>
                                                    <span className="text-xs font-bold text-gray-700">{note.author || 'Agent'}</span>
                                                    <span className="text-[10px] text-gray-400 ml-auto">{note.timestamp}</span>
                                                </div>
                                                <p className="text-sm text-gray-700 leading-relaxed pl-9">{note.content}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Existing saved agent notes - text format for view_enquiry/view_offer */}
                            {(mode === "view_enquiry" ? enqForm.agent_notes : mode === "view_offer" ? offerForm.agent_notes : null) && (
                                <div className="mb-6">
                                    <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest mb-3">Agent Notes</p>
                                    <AgentNotesSummary
                                        text={mode === "view_enquiry" ? enqForm.agent_notes : offerForm.agent_notes}
                                        subject={lead?.name || lead?.full_name}
                                        className="mb-3"
                                    />
                                    <div className="bg-gray-50/80 border border-gray-200 rounded-2xl p-4 space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
                                        {(mode === "view_enquiry" ? enqForm.agent_notes : offerForm.agent_notes)
                                            .split(/\n\n+/)
                                            .filter(Boolean)
                                            .reverse()
                                            .map((entry, i) => {
                                                const match = entry.match(/^\[([^\]]+)\]\s*(?:\(([^)]+)\))?\s*(.*)$/s);
                                                const timestamp = match ? match[1] : '';
                                                const author = match ? (match[2] || 'Agent') : 'Agent';
                                                const content = match ? match[3] : entry;
                                                return (
                                                    <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="w-7 h-7 rounded-full bg-[#55C5D0]/20 flex items-center justify-center">
                                                                <User size={14} className="text-[#55C5D0]" />
                                                            </div>
                                                            <span className="text-xs font-bold text-gray-700">{author}</span>
                                                            {timestamp && <span className="text-[10px] text-gray-400 ml-auto">{timestamp}</span>}
                                                        </div>
                                                        <p className="text-sm text-gray-700 leading-relaxed pl-9">{content}</p>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                            )}

                            {/* Add new note */}
                            <div className="flex flex-col gap-3 mt-2">
                                <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest">Add New Note / Log Negotiation</p>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    className="w-full px-5 py-4 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 outline-none focus:ring-4 focus:ring-[#55C5D0]/10 focus:border-[#55C5D0] transition-all shadow-sm"
                                    placeholder="Add follow-up notes or internal comments here..."
                                />
                                {/* The call-back series, directly under the note and booked
                                    by the same button (Christian Gravias, Monday 13166757620). */}
                                {showReminderSeries && (
                                    <React.Suspense fallback={null}>
                                        <AppraisalReminderSeries
                                            key={`${lead.id}-${reminderKey}`}
                                            fetchBooked={fetchBooked}
                                            deleteBooked={deleteLeadReminder ? deleteBooked : undefined}
                                            value={reminders}
                                            onChange={setReminders}
                                            pendingLabel="Saving with this note"
                                        />
                                    </React.Suspense>
                                )}
                                <button
                                    onClick={handleAddNote}
                                    type="button"
                                    disabled={
                                        addNoteLoading ||
                                        (!(notes?.trim() && onAddNote) && reminders.length === 0)
                                    }
                                    className="w-fit flex items-center gap-2 px-6 py-2.5 bg-[#55C5D0] text-white text-[13px] font-bold rounded-xl transition-all shadow-md active:scale-95 disabled:opacity-50"
                                >
                                    {addNoteLoading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} />}
                                    {reminders.length === 0
                                        ? "Add Note"
                                        : notes?.trim()
                                            ? "Add Note & Reminders"
                                            : "Save Reminders"}
                                </button>
                            </div>
                        </section>
                    </div>
                </div>

                {/* Footer Section — listing actions align with Ignore / Add to Property */}
                <div className="px-10 py-6 bg-white border-t border-gray-100 flex flex-wrap items-center justify-end gap-3 flex-shrink-0">
                    {effectiveOffer && showOfferWorkflow && (
                        <OfferWorkflowActions
                            offer={effectiveOffer}
                            propertyId={propertyId}
                            buyerName={displayName}
                            buyerEmail={displayEmail}
                            showHistory
                            counterPrefill={{
                                counter_amount: offerForm.counter_amount || effectiveOffer.counter_amount,
                                offer_amount: offerForm.offer_amount || effectiveOffer.offer_amount,
                                deposit_amount: offerForm.deposit_amount || effectiveOffer.deposit_amount,
                                settlement_days: offerForm.settlement_days || effectiveOffer.settlement_days,
                                custom_settlement_date: offerForm.custom_settlement_date || effectiveOffer.custom_settlement_date,
                            }}
                            onOfferUpdated={applyOfferUpdate}
                            onDecline={() => { if (onDecline) onDecline(lead); else onClose(); }}
                            onBeforeContractNav={onClose}
                        />
                    )}

                    {mode === "assign" && !isOffer && !isAgentApplication && (
                        <button
                            type="button"
                            onClick={() => {
                                onOpenPreferences?.(lead);
                                onClose();
                            }}
                            className="px-8 py-3 text-sm font-bold text-[#3a99a3] border border-[#55C5D0] rounded-xl hover:bg-[#55C5D0]/10 transition-all inline-flex items-center gap-2 font-urbanist"
                        >
                            <SlidersHorizontal size={16} />
                            {lead.lead_type === 'renter_enquiry' ? 'Renter Preferences' : 'Buying Preferences'}
                        </button>
                    )}

                    {/* File the person straight into a Database category. Some enquiries
                        are worth keeping as a contact even when there is no listing to
                        attach them to (Christian Gravias, Monday 12849654308). */}
                    {mode === "assign" && !isOffer && (
                        <button
                            type="button"
                            onClick={() => setDbLead({
                                id: lead?.id,
                                name: displayName || "",
                                email: displayEmail || "",
                                phone: displayPhone || "",
                                property_address: selectedProperty
                                    ? (selectedProperty.full_address || selectedProperty.address || "")
                                    : (leadForm.address && leadForm.address !== "N/A" ? leadForm.address : ""),
                            })}
                            className="px-8 py-3 text-sm font-bold text-[#3a99a3] border border-[#55C5D0] rounded-xl hover:bg-[#55C5D0]/10 transition-all inline-flex items-center gap-2 font-urbanist"
                        >
                            <Database size={16} />
                            Add to Database
                        </button>
                    )}

                    <button
                        onClick={() => {
                            if (onIgnore) {
                                onIgnore(lead);
                                onClose();
                            } else {
                                onClose();
                            }
                        }}
                        className="px-10 py-3 text-sm font-bold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all font-urbanist"
                    >
                        {mode === "assign" ? (isAgentApplication ? "Ignore Application" : "Ignore Enquiry") : "Close"}
                    </button>

                    {/* Offers are auto-filed onto the listing, so the manual "Add to Property" action is hidden for them */}
                    {!(mode === "assign" && (isOffer || isAgentApplication)) && (
                        <button
                            onClick={async () => {
                                if (mode === "assign") {
                                    // Reminders staged but not yet saved are booked first, so
                                    // filing the enquiry onto a listing does not drop them.
                                    await bookStagedReminders(notes?.trim() || "");
                                    onAssign && onAssign(selectedPropertyId, notes, leadForm, offerForm);
                                }
                                else {
                                    if (mode === "view_enquiry" && onSaveEnquiry) onSaveEnquiry(enqForm);
                                    if (mode === "view_offer" && onSaveOffer) onSaveOffer(offerForm);
                                }
                            }}
                            disabled={loading || (mode === "assign" && !selectedPropertyId)}
                            className="px-10 py-3 bg-[#55C5D0] text-white text-sm font-bold rounded-xl shadow-lg shadow-[#55C5D0]/20 hover:bg-[#46acb6] transition-all flex items-center gap-2 disabled:opacity-50 font-urbanist"
                        >
                            {loading && <Loader2 size={18} className="animate-spin" />}
                            {loading ? "Processing..." : (mode === "assign" ? "Add to Property" : "Update Records")}
                        </button>
                    )}
                </div>
            </div>

            {dbLead && (
                <React.Suspense fallback={null}>
                    <AddToDatabaseModal
                        isOpen
                        lead={dbLead}
                        defaultView="buyers"
                        noteLabel="From Buyer Enquiries"
                        onClose={() => setDbLead(null)}
                        onSaved={(savedLead) => {
                            setDbLead(null);
                            onDatabaseSaved && onDatabaseSaved(savedLead);
                            onClose();
                        }}
                    />
                </React.Suspense>
            )}

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }
            `}</style>
        </div>
    );
};

export default LeadPreviewModal;
